import { useCallback, useState } from 'react';

/**
 * Les blocs du tableau de bord que la personne peut réordonner, dans l'ordre
 * par défaut. Un bloc ajouté plus tard à cette liste apparaît à sa place par
 * défaut dans un ordre déjà enregistré ; un bloc retiré en disparaît.
 */
export const MODULES_TABLEAU = [
  'prevision',
  'jour',
  'apercu',
  'aprevenir',
  'suivis',
  'introuvables',
  'site',
] as const;

export type IdModule = (typeof MODULES_TABLEAU)[number];

const connus: ReadonlySet<string> = new Set(MODULES_TABLEAU);

/**
 * Ordre complet (tous les blocs connus, visibles ou non) à partir de l'ordre
 * enregistré : on écarte les blocs inconnus et les doublons, puis chaque bloc
 * absent de l'enregistrement se glisse juste après son prédécesseur de l'ordre
 * par défaut (ou en tête s'il ouvre la liste).
 */
export function fusionnerOrdre(enregistre: readonly string[] | null): IdModule[] {
  const ordre: IdModule[] = [];
  for (const id of enregistre ?? []) {
    if (connus.has(id) && !ordre.includes(id as IdModule)) ordre.push(id as IdModule);
  }
  MODULES_TABLEAU.forEach((id, i) => {
    if (ordre.includes(id)) return;
    const precedent = i > 0 ? ordre.indexOf(MODULES_TABLEAU[i - 1]!) : -1;
    ordre.splice(precedent + 1, 0, id);
  });
  return ordre;
}

/**
 * Remplace, dans l'ordre complet, les places occupées par les blocs visibles
 * par `nouvelOrdre` (les blocs masqués — droits, données absentes — gardent
 * leur place pour le jour où ils reviennent).
 */
export function appliquerOrdreVisible(
  complet: readonly IdModule[],
  nouvelOrdre: readonly IdModule[],
): IdModule[] {
  const visibles = new Set(nouvelOrdre);
  let suivant = 0;
  return complet.map((id) => (visibles.has(id) ? nouvelOrdre[suivant++]! : id));
}

/** Déplace l'élément `de` vers `vers` (indices bornés) sans muter la liste. */
export function deplacer<T>(liste: readonly T[], de: number, vers: number): T[] {
  const copie = [...liste];
  if (de < 0 || de >= copie.length) return copie;
  const cible = Math.max(0, Math.min(copie.length - 1, vers));
  const [element] = copie.splice(de, 1);
  copie.splice(cible, 0, element!);
  return copie;
}

/**
 * L'ordre est une préférence locale, par compte et par galerie (le même
 * compte peut avoir plusieurs galeries), comme « Bientôt dans votre régie »
 * (`preferences/bientotStorage.ts`) : rien n'est écrit côté serveur. Stockage
 * refusé ou plein : le choix reste actif pour la page courante.
 */
export function ordreStorageKey(compteId: string, galerieSlug: string): string {
  return `artosera-tdb-ordre:${encodeURIComponent(compteId)}:${encodeURIComponent(galerieSlug)}`;
}

/** `null` = effacé (ordre par défaut) ; absent de la mémoire = voir le stockage. */
const volatile = new Map<string, readonly string[] | null>();

export function readOrdreModules(compteId: string, galerieSlug: string): IdModule[] {
  const cle = ordreStorageKey(compteId, galerieSlug);
  if (volatile.has(cle)) return fusionnerOrdre(volatile.get(cle) ?? null);
  try {
    const brut = window.localStorage.getItem(cle);
    const valeur: unknown = brut ? JSON.parse(brut) : null;
    if (Array.isArray(valeur) && valeur.every((v) => typeof v === 'string')) {
      return fusionnerOrdre(valeur as string[]);
    }
  } catch {
    // Stockage inaccessible ou contenu illisible : ordre par défaut.
  }
  return fusionnerOrdre(null);
}

/** Enregistre l'ordre ; `null` revient à l'ordre par défaut. */
export function storeOrdreModules(
  compteId: string,
  galerieSlug: string,
  ordre: readonly IdModule[] | null,
): void {
  const cle = ordreStorageKey(compteId, galerieSlug);
  try {
    if (ordre) window.localStorage.setItem(cle, JSON.stringify(ordre));
    else window.localStorage.removeItem(cle);
    volatile.delete(cle);
  } catch {
    volatile.set(cle, ordre);
  }
}

/**
 * L'ordre complet des blocs, lu une fois par compte et par galerie, puis tenu
 * en état. `changer(null)` rétablit l'ordre par défaut.
 */
export function useOrdreModules(
  compteId: string,
  galerieSlug: string,
): [IdModule[], (ordre: readonly IdModule[] | null) => void] {
  const cle = ordreStorageKey(compteId, galerieSlug);
  const [etat, setEtat] = useState(() => ({ cle, ordre: readOrdreModules(compteId, galerieSlug) }));
  // Changement de compte ou de galerie page ouverte : on relit pour la nouvelle clé.
  const ordre = etat.cle === cle ? etat.ordre : readOrdreModules(compteId, galerieSlug);
  const changer = useCallback(
    (nouvel: readonly IdModule[] | null): void => {
      storeOrdreModules(compteId, galerieSlug, nouvel);
      setEtat({ cle, ordre: nouvel ? [...nouvel] : fusionnerOrdre(null) });
    },
    [compteId, galerieSlug, cle],
  );
  return [ordre, changer];
}
